/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { type QueryClient, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { useEffect, useSyncExternalStore } from 'react';
import { useAuth } from '../../auth';
import { toast } from '../../paToast';
import { getAgentTurn, getAgentWorkspace, saveAgentWorkspace } from '../api';
import { turnMessages } from '../session/selectors';
import type { AgentMessage } from '../types';
import { conversationIdFromPath } from './AgentWorkspaceContext';
import { agentWorkspaceStore, toWorkspacePayload } from './agentWorkspaceStore';
import { resolveArtifactSource } from './artifactSource';
import type { AgentWorkspace, AgentWorkspaceWidget } from './types';

// Edits (drags, resizes, expands) settle before they're saved.
const SAVE_DELAY_MS = 800;
// A failed save (outage, or another tab saving in between twice) retries with backoff:
// 2s, 4s, 8s, 16s, then gives up until the next edit and says so.
const MAX_SAVE_ATTEMPTS = 5;
const retryDelayMs = (failures: number) => Math.min(30_000, 2_000 * 2 ** (failures - 1));

const workspaceQueryKey = (conversationId: string, user: string | undefined) =>
  ['agent-workspace', conversationId, user] as const;

/**
 * The server copy with every widget rebuilt from its turn. Widgets whose turn or part is gone
 * are dropped, with their layout.
 */
const loadServerWorkspace = async (
  conversationId: string,
  user: string | undefined,
): Promise<AgentWorkspace & { revision: number }> => {
  const server = await getAgentWorkspace(conversationId, user);
  const turnIds = [...new Set(server.widgets.map(({ source }) => source.turn_id))];
  // Only a turn the server no longer has counts as gone; any other failure rejects the load so
  // it is retried instead of dropping the widgets for the rest of the session.
  const turns = await Promise.all(
    turnIds.map((turnId) =>
      getAgentTurn(conversationId, turnId, user).catch((error: unknown) => {
        if (error instanceof HTTPError && error.response.status === 404) return undefined;
        throw error;
      }),
    ),
  );
  const messagesByTurn = new Map<string, AgentMessage[]>();
  for (const result of turns) {
    if (result) messagesByTurn.set(result.turn.turn_id, turnMessages(result.turn, undefined));
  }
  const widgets = server.widgets.flatMap(({ id, kind, source }): AgentWorkspaceWidget[] => {
    const resolvedSource = { ...source, part_index: source.part_index ?? 0 };
    const artifact = resolveArtifactSource(
      messagesByTurn.get(source.turn_id) ?? [],
      kind,
      resolvedSource,
    );
    return artifact ? [{ id, artifact, source: resolvedSource }] : [];
  });
  const ids = new Set(widgets.map(({ id }) => id));
  return {
    title: server.title ?? undefined,
    widgets,
    layout: server.layout.filter(({ i }) => ids.has(i)),
    revision: server.revision,
  };
};

const workspaceQuery = (conversationId: string, user: string | undefined) => ({
  queryKey: workspaceQueryKey(conversationId, user),
  queryFn: () => loadServerWorkspace(conversationId, user),
  // Loaded once per conversation; afterwards this tab's saves keep the store current.
  staleTime: Number.POSITIVE_INFINITY,
});

/** Loads the conversation's saved workspace into the store (the workspace window shows it). */
export const useAgentWorkspaceServerCopy = (conversationId: string | undefined) => {
  const user = useAuth().user?.nickname;
  const { data } = useQuery({
    ...workspaceQuery(conversationId ?? '', user),
    enabled: Boolean(conversationId && user),
  });
  useEffect(() => {
    if (conversationId && data) agentWorkspaceStore.applyServer(conversationId, data);
  }, [conversationId, data]);
};

const TITLE_SEPARATOR = '\u0000';

/**
 * Names each workspace window tab after its conversation, mounted or not. Tabs this browser tab
 * hasn't seen yet load their saved workspace, which carries the title (and warms the content).
 */
export const useAgentWorkspaceTabLabels = (paths: ReadonlyArray<string>) => {
  const user = useAuth().user?.nickname;
  const ids = paths.map((path) =>
    conversationIdFromPath(new URL(path, 'http://desktop.local').pathname),
  );
  const titles = useSyncExternalStore(agentWorkspaceStore.subscribe, () =>
    ids.map((id) => (id && agentWorkspaceStore.get(id).title) || '').join(TITLE_SEPARATOR),
  ).split(TITLE_SEPARATOR);
  const untitled = ids.filter((id, index): id is string => Boolean(id) && !titles[index]);
  const loaded = useQueries({
    queries: untitled.map((id) => ({ ...workspaceQuery(id, user), enabled: Boolean(user) })),
    combine: (results) => results.map(({ data }) => data),
  });
  useEffect(() => {
    untitled.forEach((id, index) => {
      const data = loaded[index];
      if (data) agentWorkspaceStore.applyServer(id, data);
    });
  }, [loaded, untitled]);
  return titles.map((title) => title || undefined);
};

/**
 * Saves the conversation's workspace; false when it still conflicted after merging. `isCurrent`
 * is checked after every request: once the account changed or the tab's Agent data was cleared,
 * nothing may be written back into the store.
 */
const saveWorkspace = async (
  queryClient: QueryClient,
  conversationId: string,
  user: string | undefined,
  isCurrent: () => boolean,
): Promise<boolean> => {
  // Two tries: a conflict means another tab saved; merge its copy in and save on top of it.
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (attempt > 0)
      await queryClient.invalidateQueries({ queryKey: workspaceQueryKey(conversationId, user) });
    const server = await queryClient.fetchQuery(workspaceQuery(conversationId, user));
    if (!isCurrent()) return true;
    agentWorkspaceStore.applyServer(conversationId, server);
    const snapshot = agentWorkspaceStore.get(conversationId);
    const payload = toWorkspacePayload(snapshot);
    // Nothing the server can hold, and nothing saved there to clear.
    if (payload.widgets.length === 0 && payload.revision === 0) {
      agentWorkspaceStore.markSaved(conversationId, snapshot, 0);
      return true;
    }
    const result = await saveAgentWorkspace(conversationId, user, payload);
    if (!isCurrent()) return true;
    if (result.status === 'saved') {
      agentWorkspaceStore.markSaved(conversationId, snapshot, result.workspace.revision);
      queryClient.setQueryData(workspaceQueryKey(conversationId, user), {
        ...snapshot,
        revision: result.workspace.revision,
      });
      return true;
    }
  }
  return false;
};

/** Saves workspace edits to the server shortly after they happen, one request at a time. */
export const useAgentWorkspaceAutosave = () => {
  const user = useAuth().user?.nickname;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return undefined;
    const timers = new Map<string, number>();
    const inFlight = new Set<string>();
    const failures = new Map<string, number>();
    // Cleared on cleanup (account change, unmount): saves still in flight then stop touching the
    // store, so a cleared tab never gets the previous account's workspace written back.
    let active = true;

    const schedule = (conversationId: string, delay = SAVE_DELAY_MS) => {
      window.clearTimeout(timers.get(conversationId));
      timers.set(
        conversationId,
        window.setTimeout(() => void flush(conversationId), delay),
      );
    };
    // A new edit starts the attempts over.
    const onEdit = (conversationId: string) => {
      failures.delete(conversationId);
      schedule(conversationId);
    };
    const onSaved = (conversationId: string) => {
      failures.delete(conversationId);
      // Edits made while saving go out next.
      if (agentWorkspaceStore.isDirty(conversationId) && !timers.has(conversationId))
        schedule(conversationId);
    };
    const onFailed = (conversationId: string) => {
      const count = (failures.get(conversationId) ?? 0) + 1;
      failures.set(conversationId, count);
      if (count < MAX_SAVE_ATTEMPTS) {
        schedule(conversationId, retryDelayMs(count));
        return;
      }
      toast.error(
        'Your workspace changes couldn’t be saved. They’re kept in this tab and saved with your next change.',
        { toastId: `agent-workspace-save-${conversationId}` },
      );
    };
    const flush = async (conversationId: string) => {
      timers.delete(conversationId);
      // An edit during a save is picked up when that save finishes.
      if (inFlight.has(conversationId)) return;
      inFlight.add(conversationId);
      // Logout clears the store without changing the account: a reset ends the save too.
      const epoch = agentWorkspaceStore.epoch();
      const isCurrent = () => active && agentWorkspaceStore.epoch() === epoch;
      const saved = await saveWorkspace(queryClient, conversationId, user, isCurrent).catch(
        () => false,
      );
      inFlight.delete(conversationId);
      if (!isCurrent()) return;
      if (saved) onSaved(conversationId);
      else onFailed(conversationId);
    };

    const unsubscribe = agentWorkspaceStore.subscribeChanges(onEdit);
    // Edits a reload interrupted (or that kept failing) are saved again without a new edit.
    for (const conversationId of agentWorkspaceStore.pendingConversationIds())
      onEdit(conversationId);
    return () => {
      active = false;
      unsubscribe();
      for (const timer of timers.values()) window.clearTimeout(timer);
    };
  }, [queryClient, user]);
};
